import { useCallback, useEffect, useState } from 'react';
import { networkApi, NetworkStatusResponse, NetworkStatusValue } from '../api/networkApi';

type Availability = 'online' | 'offline' | 'backend-unreachable' | 'internet-unavailable';

interface NetworkState {
  availability: Availability;
  message: string;
  checkedAt?: string;
}

const isBackendOnline = (status: NetworkStatusValue) =>
  status === 'InternetAvailable' || status === 3;

const getUnavailableMessage = (status?: NetworkStatusResponse) => {
  if (!status) {
    return 'Cannot reach the HRMS server.';
  }

  if (status.status === 'NoNetwork' || status.status === 1) {
    return 'The HRMS server has no network connection.';
  }

  if (status.status === 'NetworkNoInternet' || status.status === 2) {
    return status.message || 'The HRMS server network is up, but internet access is unavailable.';
  }

  return status.message || 'Cannot reach the HRMS server.';
};

export const useNetworkStatus = () => {
  const [state, setState] = useState<NetworkState>(() => ({
    availability: navigator.onLine ? 'online' : 'offline',
    message: navigator.onLine ? '' : 'No internet connection.',
  }));

  const checkStatus = useCallback(async () => {
    if (!navigator.onLine) {
      setState({
        availability: 'offline',
        message: 'No internet connection.',
      });
      return;
    }

    try {
      const status = await networkApi.getStatus();

      if (status.isInternetAvailable === true || isBackendOnline(status.status)) {
        setState({
          availability: 'online',
          message: '',
          checkedAt: status.checkedAtUtc,
        });
        return;
      }

      setState({
        availability: 'internet-unavailable',
        message: getUnavailableMessage(status),
        checkedAt: status.checkedAtUtc,
      });
    } catch {
      setState({
        availability: 'backend-unreachable',
        message: 'Cannot reach the HRMS server.',
      });
    }
  }, []);

  useEffect(() => {
    void checkStatus();

    const handleOnline = () => {
      void checkStatus();
    };
    const handleOffline = () => {
      setState({
        availability: 'offline',
        message: 'No internet connection.',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const intervalId = window.setInterval(() => {
      void checkStatus();
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.clearInterval(intervalId);
    };
  }, [checkStatus]);

  return {
    ...state,
    isOnline: state.availability === 'online',
    refresh: checkStatus,
  };
};
